import { Box, Flex, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { LuCompass, LuMessageCircle } from "react-icons/lu";
import { NavLink, useMatch } from "react-router-dom";
import { TopBar } from "src/components/TopBar";
import { useChatStore } from "src/stores/chatStoreContext";
import { tap } from "src/utils/haptic";

/** Phone-width column: full width on phones, a centred app column on desktop. */
export const COLUMN_MAX_W = "480px";
/** Space pages leave at the bottom for the tab bar. */
export const TAB_BAR_SPACE = "calc(84px + env(safe-area-inset-bottom))";

const TABS = [
  { to: "/", label: "Discover", icon: LuCompass },
  { to: "/chats", label: "Chats", icon: LuMessageCircle },
] as const;

const TabBar = observer(() => {
  const chatStore = useChatStore();
  const chatCount = chatStore.recentChats.length;

  return (
    <Flex
      as="nav"
      position="fixed"
      bottom={0}
      left="50%"
      transform="translateX(-50%)"
      width="100%"
      maxW={COLUMN_MAX_W}
      zIndex={20}
      bg="rgba(14,10,10,0.82)"
      backdropFilter="saturate(180%) blur(20px)"
      borderTopWidth="1px"
      borderColor="line"
      pt={2}
      pb="calc(8px + env(safe-area-inset-bottom))"
      justify="space-around"
    >
      {TABS.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={tap}
          style={{ flex: 1 }}
        >
          {({ isActive }) => (
            <Flex
              direction="column"
              align="center"
              gap={1}
              py={1}
              color={isActive ? "white" : "faint"}
              transition="color 0.2s, transform 0.15s"
              _active={{ transform: "scale(0.92)" }}
              position="relative"
            >
              <Box position="relative">
                <Icon size={24} strokeWidth={isActive ? 2.4 : 1.8} />
                {to === "/chats" && chatCount > 0 && (
                  <Box
                    position="absolute"
                    top="-4px"
                    right="-8px"
                    minW="16px"
                    h="16px"
                    px="4px"
                    borderRadius="full"
                    bg="accent"
                    color="white"
                    fontSize="10px"
                    fontWeight="bold"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {chatCount}
                  </Box>
                )}
              </Box>
              <Text
                fontSize="10px"
                fontWeight={isActive ? "semibold" : "medium"}
                letterSpacing="0.02em"
              >
                {label}
              </Text>
            </Flex>
          )}
        </NavLink>
      ))}
    </Flex>
  );
});

/** Shell of the signed-in app: a phone column with a bottom tab bar. */
export function AppLayout({ children }: React.PropsWithChildren) {
  // The chat is full screen: no tab bar.
  const inChat = useMatch("/chat/*");
  const inSubPage = useMatch("/settings/*");
  const showTabs = !inChat && !inSubPage;

  return (
    <Box bg="app" minH="100dvh">
      <Box
        maxW={COLUMN_MAX_W}
        mx="auto"
        minH="100dvh"
        position="relative"
        bg="app"
        borderXWidth={{ base: 0, md: "1px" }}
        borderColor="line"
        display="flex"
        flexDirection="column"
      >
        {showTabs && (
          <Flex
            position="sticky"
            top={0}
            zIndex={25}
            justify="center"
            pt="calc(10px + env(safe-area-inset-top))"
            pb={3}
            bg="linear-gradient(180deg, #0e0a0a 60%, rgba(14,10,10,0))"
          >
            <TopBar />
          </Flex>
        )}
        {children}
      </Box>
      {showTabs && <TabBar />}
    </Box>
  );
}

/** Disclaimer and company line, small, at the bottom of a page. */
export function Disclaimer() {
  return (
    <Text fontSize="11px" color="faint" lineHeight="1.6" px={5} pt={6} pb={2}>
      <strong>Important:</strong> Lovelica is an AI-powered emotional support
      and self-help app. It is not a licensed therapist, psychologist,
      psychiatrist, counselor or healthcare provider, and does not provide
      medical advice, diagnosis or treatment. In a mental health crisis or
      emergency, contact 988, 911 or your local emergency services. Lovelica is
      operated by Chimecorner Inc, 304 S Jones Blvd Ste 7773, Las Vegas, NV
      89107, United States.
    </Text>
  );
}
